"use client";

import { useState, useEffect, useRef } from "react";
import { Award, X, Lightbulb, Paperclip } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { EXPLORING_CERTIFICATION, isExploringCertification } from "@/lib/exploring-certification";

const SUGGEST_ATTACHMENT_ACCEPT =
  "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png";
const SUGGEST_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

interface CertificationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /**
   * When true, the field accepts arbitrary typed text instead of forcing an
   * exact catalog match. Used ONLY by the tutor application, where a tutor may
   * list a credential not yet in the catalog — it triggers a cert request that
   * an admin reviews before the tutor can be approved. Everywhere else this is
   * false so a user cannot inject an uncurated cert into their profile.
   */
  allowFreeText?: boolean;
  /** Prefill guest suggest-request email (tutor apply, founding apply). */
  applicantEmail?: string;
  /** Pin "Exploring certifications" for journey and onboarding only. */
  allowExploring?: boolean;
  /** Onboarding: keep the list open and check off as many catalog exams as needed. */
  multiple?: boolean;
  selected?: string[];
  onSelectedChange?: (next: string[]) => void;
}

export function CertificationAutocomplete({
  value,
  onChange,
  placeholder = "Select certification",
  className = "",
  inputClassName,
  allowFreeText = false,
  applicantEmail,
  allowExploring = false,
  multiple = false,
  selected = [],
  onSelectedChange,
}: CertificationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  // The last value we actually committed upstream (a catalog match, a cleared
  // value, or a just-submitted suggestion). Typed-but-uncommitted text is never
  // persisted — on blur we revert to this so only real catalog certs stick.
  const [committedValue, setCommittedValue] = useState(value);
  const [hint, setHint] = useState("");
  const [allCertifications, setAllCertifications] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestName, setSuggestName] = useState("");
  const [suggestOrg, setSuggestOrg] = useState("");
  const [suggestNote, setSuggestNote] = useState("");
  const [suggestRefUrl, setSuggestRefUrl] = useState("");
  const [suggestFile, setSuggestFile] = useState<File | null>(null);
  const [suggestEmail, setSuggestEmail] = useState(applicantEmail || "");
  const [suggestError, setSuggestError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetSuggestForm = () => {
    setSuggestName("");
    setSuggestOrg("");
    setSuggestNote("");
    setSuggestRefUrl("");
    setSuggestFile(null);
    setSuggestEmail(applicantEmail || "");
    setSuggestError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSuggestFile(null);
      return;
    }
    if (file.size > SUGGEST_ATTACHMENT_MAX_BYTES) {
      setSuggestError("Attachment is too large. Maximum size is 10MB.");
      e.target.value = "";
      return;
    }
    setSuggestError("");
    setSuggestFile(file);
  };

  // Fetch the full list on mount — fixes: was checking data.success (always undefined)
  useEffect(() => {
    fetch("/api/certifications/list")
      .then((r) => r.json())
      .then((data) => {
        // API returns { certifications: string[] } — no success flag needed
        if (Array.isArray(data.certifications)) {
          setAllCertifications(data.certifications);
        }
      })
      .catch(() => {});
  }, []);

  // Sync controlled value from the parent. Multi-select uses the box as a filter only.
  useEffect(() => {
    if (multiple) return;
    setInputValue(value);
    setCommittedValue(value);
  }, [multiple, value]);

  const toggleSelected = (cert: string) => {
    const exploring = isExploringCertification(cert);
    const has = selected.some((item) => item.toLowerCase() === cert.toLowerCase());
    let next: string[];
    if (exploring) {
      next = has ? [] : [EXPLORING_CERTIFICATION];
    } else if (has) {
      next = selected.filter((item) => item.toLowerCase() !== cert.toLowerCase());
    } else {
      next = [...selected.filter((item) => !isExploringCertification(item)), cert];
    }
    onSelectedChange?.(next);
    setInputValue("");
    setHint("");
    setIsOpen(true);
    inputRef.current?.focus();
  };

  // Filtered list: show all when empty, filter when typing
  const catalogFiltered = inputValue.trim().length === 0
    ? allCertifications
    : allCertifications.filter((c) =>
        c.toLowerCase().includes(inputValue.toLowerCase().trim())
      );
  const exploringVisible =
    allowExploring &&
    (inputValue.trim().length === 0 ||
      EXPLORING_CERTIFICATION.toLowerCase().includes(inputValue.trim().toLowerCase()));
  const filtered = exploringVisible
    ? [EXPLORING_CERTIFICATION, ...catalogFiltered.filter((c) => !isExploringCertification(c))]
    : catalogFiltered;

  // Commit a value upstream (catalog match, cleared, or just-suggested).
  const commit = (v: string) => {
    setCommittedValue(v);
    onChange(v);
  };

  // Case-insensitive exact match (spelling + spacing must otherwise match).
  // Returns the catalog's canonical casing when found.
  const canonicalMatch = (v: string): string | undefined => {
    const q = v.trim().toLowerCase();
    if (!q) return undefined;
    if (allowExploring && isExploringCertification(q)) return EXPLORING_CERTIFICATION;
    return allCertifications.find((c) => c.toLowerCase() === q);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    setHint("");
    setIsOpen(true);
    // Free-text mode (tutor application): persist raw text as they type.
    // Enforced mode: DON'T persist — only a real selection commits upstream.
    if (allowFreeText) commit(v);
  };

  const handleFocus = () => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
    setIsOpen(true);
  };

  const handleSelect = (cert: string) => {
    if (multiple) {
      if (blurTimeout.current) clearTimeout(blurTimeout.current);
      toggleSelected(cert);
      return;
    }
    setInputValue(cert);
    setHint("");
    commit(cert);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    setHint("");
    if (!multiple) commit("");
    setIsOpen(true); // reopen to show full list
    inputRef.current?.focus();
  };

  // Enforce catalog-only selection when focus leaves the field. If the typed
  // text is an exact (case-insensitive) catalog entry, commit it; otherwise
  // revert to the last committed value and nudge the user to pick or suggest.
  const enforceSelection = () => {
    if (multiple) {
      setInputValue("");
      setHint("");
      return;
    }
    if (allowFreeText) return; // tutor application: keep whatever was typed
    if (allowExploring && isExploringCertification(inputValue)) {
      setInputValue(EXPLORING_CERTIFICATION);
      commit(EXPLORING_CERTIFICATION);
      setHint("");
      return;
    }
    if (allCertifications.length === 0) return; // list not loaded — don't discard
    const typed = inputValue.trim();
    if (typed === committedValue.trim()) return;
    const match = canonicalMatch(typed);
    if (match) {
      setInputValue(match);
      commit(match);
      setHint("");
    } else if (typed.length === 0) {
      commit("");
      setHint("");
    } else {
      setInputValue(committedValue);
      setHint("Pick a certification from the list, or suggest a new one.");
    }
  };

  const handleBlur = () => {
    // Delay so a dropdown option's onMouseDown can run before we enforce.
    blurTimeout.current = setTimeout(() => {
      setIsOpen(false);
      enforceSelection();
    }, 120);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      enforceSelection();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const match = canonicalMatch(inputValue);
      if (match) handleSelect(match);
    }
  };

  useEffect(() => {
    const closeIfLeft = (target: EventTarget | null) => {
      if (!rootRef.current?.contains(target as Node)) setIsOpen(false);
    };
    const handlePointerDown = (event: MouseEvent) => closeIfLeft(event.target);
    const handleFocusIn = (event: FocusEvent) => closeIfLeft(event.target);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  const handleSuggestSubmit = async () => {
    if (!suggestName.trim() || !suggestOrg.trim()) {
      setSuggestError("Certification name and organization are required.");
      return;
    }
    if (!isAuthenticated && !(applicantEmail || suggestEmail).trim()) {
      setSuggestError("Please enter your email so we can follow up on this suggestion.");
      return;
    }

    const trimmedRefUrl = suggestRefUrl.trim();
    if (trimmedRefUrl) {
      try {
        const parsed = new URL(trimmedRefUrl);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          throw new Error("invalid protocol");
        }
      } catch {
        setSuggestError("Reference link must be a valid http(s) URL.");
        return;
      }
    }

    setSuggestError("");

    let attachmentUrl: string | null = null;
    if (suggestFile) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", suggestFile);
        const uploadRes = await fetch("/api/upload", {
          method: "POST",
          body: formData,
        });
        if (!uploadRes.ok) {
          let message = "Failed to upload attachment.";
          try {
            const errBody = await uploadRes.json();
            if (errBody?.error) message = errBody.error;
          } catch {
            // Response wasn't JSON; keep generic message
          }
          setSuggestError(message);
          setIsUploading(false);
          return;
        }
        const uploadData = await uploadRes.json();
        attachmentUrl = uploadData?.url || null;
      } catch {
        setSuggestError("Failed to upload attachment. Please try again.");
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    setIsSubmitting(true);
    const submittedName = suggestName.trim();
    try {
      const res = await fetch("/api/certification-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: submittedName,
          organization: suggestOrg.trim(),
          note: suggestNote.trim(),
          attachmentUrl,
          referenceUrl: trimmedRefUrl || null,
          ...(!isAuthenticated
            ? { email: (applicantEmail || suggestEmail).trim(), requesterName: submittedName }
            : {}),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowSuggestModal(false);
        resetSuggestForm();
        // Optimistically adopt the suggested name (shown as "pending review" by
        // the parent surface). It does NOT enter the global catalog until an
        // admin approves the request.
        setInputValue(multiple ? "" : submittedName);
        setHint("");
        if (!multiple) commit(submittedName);
        toast({ title: "Suggestion submitted", description: "Our team will review your certification suggestion. We'll set it on your profile as pending in the meantime." });
      } else {
        setSuggestError(data.error || "Failed to submit request.");
      }
    } catch {
      setSuggestError("Failed to submit request. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {multiple && selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((cert) => (
            <button
              key={cert}
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              onMouseDown={(e) => {
                e.preventDefault();
                toggleSelected(cert);
              }}
            >
              {cert}
              <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <Award className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={multiple ? "Search for a certification…" : placeholder}
          className={["pl-10 pr-10 bg-white", inputClassName].filter(Boolean).join(" ")}
          autoComplete="off"
        />
        {inputValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {hint && (
        <p className="mt-1 text-xs text-amber-600">{hint}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-[80] mt-1 flex w-full flex-col overflow-hidden rounded-md border border-border bg-white text-gray-900 shadow-lg"
        >
          <div className="max-h-40 overflow-y-auto">
          {filtered.length > 0 ? (
            filtered.slice(0, 50).map((cert) => {
              const checked = multiple && selected.some((item) => item.toLowerCase() === cert.toLowerCase());
              return (
              <button
                key={cert}
                type="button"
                className="flex w-full items-center gap-2 border-b border-border px-4 py-2 text-left text-sm text-gray-900 last:border-b-0 hover:bg-muted focus:bg-muted focus:outline-none"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(cert); }}
              >
                {multiple ? (
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-white"}`}>
                    {checked ? "✓" : ""}
                  </span>
                ) : (
                  <Award className="h-3 w-3 shrink-0 text-gray-400" />
                )}
                {cert}
              </button>
              );
            })
          ) : (
            <div className="px-4 py-3 text-center text-sm text-gray-500">
              {allCertifications.length === 0
                ? "Loading certifications…"
                : `No certifications found for “${inputValue}”`}
            </div>
          )}
          </div>

          <div className="shrink-0 border-t border-border p-2">
            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-50 transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                if (blurTimeout.current) clearTimeout(blurTimeout.current);
                if (inputValue.trim()) setSuggestName(inputValue.trim());
                if (applicantEmail) setSuggestEmail(applicantEmail);
                setIsOpen(false);
                setShowSuggestModal(true);
              }}
            >
              <Lightbulb className="h-4 w-4" />
              Suggest a certification
            </button>
          </div>
        </div>
      )}

      {/* Suggest Certification Modal */}
      <Dialog
        open={showSuggestModal}
        onOpenChange={(open) => {
          setShowSuggestModal(open);
          if (!open) resetSuggestForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request this certification</DialogTitle>
            <DialogDescription>
              Can&apos;t find yours? Requesting it asks us to add an exam page,
              study groups, and a listing in search so others preparing for it
              can find each other.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="suggest-cert-name">Certification Name *</Label>
              <Input id="suggest-cert-name" value={suggestName} onChange={(e) => setSuggestName(e.target.value)} placeholder="e.g. AWS Certified Solutions Architect" />
            </div>
            {!isAuthenticated && !applicantEmail && (
              <div>
                <Label htmlFor="suggest-cert-email">Your email *</Label>
                <Input
                  id="suggest-cert-email"
                  type="email"
                  value={suggestEmail}
                  onChange={(e) => setSuggestEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            )}
            <div>
              <Label htmlFor="suggest-cert-org">Certifying Organization *</Label>
              <Input id="suggest-cert-org" value={suggestOrg} onChange={(e) => setSuggestOrg(e.target.value)} placeholder="e.g. Amazon Web Services" />
            </div>
            <div>
              <Label htmlFor="suggest-cert-note">Additional Notes (optional)</Label>
              <Input id="suggest-cert-note" value={suggestNote} onChange={(e) => setSuggestNote(e.target.value)} placeholder="Any additional info for the admin reviewer" />
            </div>
            <div>
              <Label htmlFor="suggest-cert-link">Reference link (optional)</Label>
              <Input
                id="suggest-cert-link"
                type="url"
                value={suggestRefUrl}
                onChange={(e) => setSuggestRefUrl(e.target.value)}
                placeholder="https://official-cert-page.com"
              />
            </div>
            <div>
              <Label htmlFor="suggest-cert-file">Attachment (optional)</Label>
              <p className="text-xs text-muted-foreground mb-1">PDF, DOC, DOCX, JPG, or PNG. Max 10MB.</p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || isSubmitting}
                >
                  <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                  {suggestFile ? "Change file" : "Choose file"}
                </Button>
                {suggestFile && (
                  <>
                    <span className="text-xs text-gray-600 truncate flex-1" title={suggestFile.name}>
                      {suggestFile.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSuggestFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      disabled={isUploading || isSubmitting}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                id="suggest-cert-file"
                type="file"
                className="hidden"
                accept={SUGGEST_ATTACHMENT_ACCEPT}
                onChange={handleFileChange}
              />
            </div>
            {suggestError && <p className="text-sm text-destructive">{suggestError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuggestModal(false)} disabled={isUploading || isSubmitting}>Cancel</Button>
            <Button
              onClick={handleSuggestSubmit}
              disabled={!suggestName.trim() || !suggestOrg.trim() || isUploading || isSubmitting}
            >
              {isUploading ? "Uploading…" : isSubmitting ? "Submitting…" : "Submit Suggestion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
